import React, { useCallback, useContext, useEffect, useState } from 'react';
import { z } from 'zod';

import Form from 'components/shared/Form';

import { ItemContext } from 'components/shared/context/ItemContextProvider';
import { ToastContext } from 'components/shared/context/ToastContextProvider';
import { UserContext } from 'components/shared/context/UserContextProvider';

import ValidatedBaseInput from 'components/shared/inputs/ValidatedBaseInput';

import BaseModal from 'components/shared/modals/BaseModal';

import Category from 'schema/category';

import sansanTech from 'services/sansantech';

interface EditCategoryModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  category: Category | null;
  resetCategory: () => void;
}

const EditCategoryModal: React.FC<EditCategoryModalProps> = ({
  isOpen,
  setIsOpen,
  category,
  resetCategory,
}) => {
  const { userState } = useContext(UserContext);
  const { toastDispatch } = useContext(ToastContext);
  const { setItem } = useContext(ItemContext);

  const [submitting, setSubmitting] = useState(false);

  const [categoryNameValue, setCategoryNameValue] = useState<string>('');
  const [isCategoryNameValueValid, setIsCategoryNameValueValid] =
    useState<boolean>(false);

  useEffect(() => {
    category?.name && setCategoryNameValue(category.name);
  }, [category]);

  const reset = useCallback(() => {
    setCategoryNameValue('');
    setIsCategoryNameValueValid(false);
    resetCategory();
  }, [resetCategory]);

  const onSubmit = useCallback(() => {
    setSubmitting(true);
    if (userState.token && category) {
      sansanTech
        .updateCategory(userState.token, category.id, {
          name: categoryNameValue,
        })
        .then((response) => {
          console.log(response);
          setItem?.((currentItem) => {
            const updatedCategory = response.data;
            if (currentItem && currentItem.categories) {
              const categoryIndex = currentItem.categories.findIndex(
                (currentItemCategory) =>
                  currentItemCategory.id === updatedCategory.id
              );
              currentItem.categories[categoryIndex].name = updatedCategory.name;
            }
            return currentItem;
          });
          setSubmitting(false);
          toastDispatch?.({
            type: 'SUCCESS',
            message: 'Edited category',
          });
          setIsOpen(false);
          reset();
        })
        .catch((error) => {
          console.log(error);
          setSubmitting(false);
          toastDispatch?.({
            type: 'ERROR',
            message: 'Could not edit category',
          });
        });
    } else {
      setSubmitting(false);
      toastDispatch?.({ type: 'ERROR', message: 'No permission to do this' });
    }
  }, [
    categoryNameValue,
    reset,
    toastDispatch,
    userState.token,
    setIsOpen,
    category,
    setItem,
  ]);

  return (
    <BaseModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      header="Edit category"
      onClose={reset}
    >
      <Form
        header="Edit category"
        onSubmit={onSubmit}
        submitting={submitting}
        disabled={!isCategoryNameValueValid}
        submitButtonText="Edit category"
        submitButtonWidth="140px"
      >
        <ValidatedBaseInput
          label="category name"
          type="text"
          value={categoryNameValue}
          setValue={setCategoryNameValue}
          setIsValid={setIsCategoryNameValueValid}
          validators={z.string().min(1, { message: 'This field is required' })}
        />
      </Form>
    </BaseModal>
  );
};

export default EditCategoryModal;
